import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalysisStore } from '../../store/analysisStore';
import { assessContent } from '../../utils/api';
import { Provider } from '../../types/analysis';

export default function AssessmentForm() {
  const [input, setInput] = useState('');
  const [inputType, setInputType] = useState<'url' | 'text'>('url');
  const [provider, setProvider] = useState<Provider>('gemini');
  const navigate = useNavigate();

  const {
    setAssessment,
    setAssessmentLoading,
    setAssessmentProgress,
    setError,
    assessmentLoading,
    firebaseToken,
  } = useAnalysisStore();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!input.trim()) {
      setError('Please enter a URL or article text');
      return;
    }

    setAssessmentLoading(true, inputType === 'url' ? input.trim() : null);
    setError(null);

    try {
      const result = await assessContent(
        input.trim(),
        inputType,
        provider,
        (message) => setAssessmentProgress(message),
        firebaseToken
      );
      setAssessment(result, input.trim(), inputType, provider);
      
      const sourceId = (result as any).source_id;
      const recordId = (result as any).id;
      
      if (recordId) {
        navigate(`/assessment/${recordId}`);
      } else if (sourceId && sourceId !== 'manual-text') {
        navigate(`/dashboard/${sourceId}`);
      }
    } catch (err) {
      if (provider === 'claude') {
        setError(
          'Claude cannot provide the analysis right now, likely due to access limits. Please switch the LLM Provider to Gemini and try again.'
        );
      } else {
        setError(err instanceof Error ? err.message : 'Assessment failed');
      }
      setAssessmentLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="assess-form">
      <div className="input-type-toggle">
        <button
          type="button"
          className={`toggle-btn ${inputType === 'url' ? 'active' : ''}`}
          onClick={() => setInputType('url')}
          disabled={assessmentLoading}
        >
          URL
        </button>
        <button
          type="button"
          className={`toggle-btn ${inputType === 'text' ? 'active' : ''}`}
          onClick={() => setInputType('text')}
          disabled={assessmentLoading}
        >
          Text
        </button>
      </div>

      {inputType === 'url' ? (
        <input
          type="text"
          className="input-field"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="https://example.com/article"
          disabled={assessmentLoading}
        />
      ) : (
        <textarea
          className="input-field"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Paste article text here..."
          rows={isCompactViewport() ? 4 : 6}
          disabled={assessmentLoading}
        />
      )}

      <div className="submit-row">
        <div className="provider-select">
          <label htmlFor="provider" style={{ fontSize: '0.875rem', fontWeight: 500 }}>LLM Provider:</label>
          <select
            id="provider"
            value={provider}
            onChange={(e) => setProvider(e.target.value as Provider)}
            disabled={assessmentLoading}
            style={{ marginLeft: '0.5rem', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
          >
            <option value="gemini">Gemini</option>
            <option value="claude">Claude</option>
          </select>
        </div>
        <button
          type="submit"
          className="assess-btn"
          disabled={assessmentLoading || !input.trim()}
        >
          {assessmentLoading ? 'Assessing...' : 'Assess'}
        </button>
      </div>
    </form>
  );
}

function isCompactViewport() {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 600;
}
